import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import { FC, useState, useCallback, memo, useEffect } from 'react';
import { commonStyles } from '../../../../utils/styles/inputs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateExtractorApi, saveExtractorApi } from '../../../../utils/apis';
import MessagePopup from '../../../common/MessagePopup';
import { JsonEditor } from 'json-edit-react';
import { useDashboard } from '../../../../hooks/useDashboard';
import { useAlert } from '../../../../hooks/useAlert';
import { TrashSimple } from '@phosphor-icons/react';
import theme from '../../../../theme';
import DeleteModal from '../../../modals/DeleteModal';

interface ExtractorProperty {
  title: string;
  description: string;
}

interface SuggestedExtractor {
  description: string;
  title: string;
  properties: Record<string, ExtractorProperty>;
  type?: string;
}

// Extracted sub-components
const ExtractorHeader = memo(
  ({
    title,
    description,
    onDelete,
    showDelete,
  }: {
    title: string;
    description: string;
    onDelete?: () => void;
    showDelete?: boolean;
  }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography
          variant="h3"
          sx={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'custom.textBlue',
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: '14px',
            fontWeight: '400',
            color: 'text.secondary',
          }}
        >
          {description}
        </Typography>
      </Box>
      {showDelete && (
        <IconButton
          sx={{
            backgroundColor: '#E74C3C26',
            borderRadius: '8px',
          }}
          onClick={onDelete}
        >
          <TrashSimple
            size={20}
            color={theme.palette.error.main}
            weight="fill"
          />
        </IconButton>
      )}
    </Box>
  )
);

const ExtractorVariables = memo(
  ({ properties }: { properties: Record<string, ExtractorProperty> }) => (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="h3"
        sx={{
          fontSize: '16px',
          fontWeight: '600',
          color: 'custom.textBlue',
          mb: 0.5,
        }}
      >
        Data Extraction Variables
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontSize: '14px',
          fontWeight: '400',
          color: 'text.secondary',
        }}
      >
        The extracted variables can also be used in Calendar and Email Functions
      </Typography>
      {properties &&
        Object.entries(properties).map(([key, property]) => (
          <Box key={key} sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: '500',
                backgroundColor: 'custom.variableBg',
                p: '4px 8px',
                borderRadius: '6px',
              }}
            >
              {property?.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', p: '4px 8px' }}
            >
              {property?.description}
            </Typography>
          </Box>
        ))}
    </Box>
  )
);

const GenerateExtractor: FC = () => {
  const {
    selectedAssistant,
    selectedAssistantId,
    updateAssistantEvent,
    updateEventFunction,
  } = useDashboard();
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const [suggestedExtractor, setSuggestedExtractor] =
    useState<SuggestedExtractor | null>(null);
  const [editableExtractor, setEditableExtractor] =
    useState<SuggestedExtractor | null>(null);

  useEffect(() => {
    const extractorEvent =
      selectedAssistant?.config?.events.find(
        event => event.eventName === 'Extractor' && event.active
      ) || null;

    if (extractorEvent?.eventValue) {
      try {
        const parsedExtractor = JSON.parse(extractorEvent.eventValue);
        setDescription(parsedExtractor.description || '');
        setEditableExtractor(parsedExtractor);
      } catch (error) {
        console.error('Failed to parse extractorEvent.eventValue:', error);
        showAlert('Failed to parse extractor configuration', 'error');
      }
    } else {
      setDescription('');
      setEditableExtractor(null);
    }
  }, [selectedAssistant, showAlert]);

  // Generate Extractor
  const { mutateAsync: generateExtractor, isPending: isExtractPending } =
    useMutation({
      mutationKey: ['generate-extractor'],
      mutationFn: generateExtractorApi,
      onSuccess: ({ data }) => {
        setSuggestedExtractor(data.suggestion);
        setEditableExtractor(data.suggestion);
        showAlert('Extractor generated successfully', 'success');
      },
      onError: (err: any) => {
        showAlert(
          err?.response?.data?.message || 'Something went wrong',
          'error'
        );
      },
    });

  // Save Extractor
  const { mutateAsync: saveExtractor, isPending: isSavePending } = useMutation({
    mutationKey: ['save-extract'],
    mutationFn: saveExtractorApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      showAlert('Extractor saved successfully', 'success');
    },
    onError: (err: any) => {
      showAlert(
        err?.response?.data?.message || 'Something went wrong',
        'error'
      );
    },
  });

  // Generate Extractor
  const handleGenerateExtractor = async () => {
    await generateExtractor({ description });
  };

  // Save Extractor
  const handleSaveExtractor = useCallback(async () => {
    if (!editableExtractor || !selectedAssistantId) return;

    await saveExtractor({
      agentId: selectedAssistantId,
      name: editableExtractor.title,
      schema: editableExtractor,
    });
  }, [saveExtractor, editableExtractor, selectedAssistantId]);

  // Delete Extractor
  const handleDeleteExtractor = async () => {
    if (!selectedAssistantId) return;

    try {
      await updateEventFunction({
        agentId: selectedAssistantId,
        eventName: 'Extractor',
        value: '',
        state: false,
      });

      updateAssistantEvent('Extractor', '', false);
      setModalOpen(false);
      showAlert('Extractor removed successfully', 'success');
    } catch (err: any) {
      showAlert(
        err?.response?.data?.message || 'Something went wrong',
        'error'
      );
    }
  };

  const handleJsonChange = useCallback(
    (newData: any) => {
      try {
        setEditableExtractor(newData);
      } catch (error: any) {
        showAlert(
          error?.response?.data?.message || 'Invalid JSON format',
          'error'
        );
      }
    },
    [showAlert]
  );

  return (
    <Box>
      <Stack spacing={2}>
        {editableExtractor ? (
          <ExtractorHeader
            title={editableExtractor.title}
            description={editableExtractor.description}
            onDelete={() => setModalOpen(true)}
            showDelete={!suggestedExtractor}
          />
        ) : (
          <ExtractorHeader
            title="Generate Extractor"
            description="Generate extractor by specifying the data you need extracted"
          />
        )}
        <Divider />

        {!editableExtractor && (
          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>Description</FormLabel>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                id="assistant-name"
                name="name"
                placeholder="Describe your extraction task"
                required
                fullWidth
                variant="outlined"
                value={description}
                onChange={e => setDescription(e.target.value)}
                sx={commonStyles.textField}
              />
              <Button
                variant="contained"
                onClick={() => void handleGenerateExtractor()}
                disabled={!description}
                sx={{
                  textTransform: 'none',
                  py: 1,
                  px: 2,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                }}
                size="small"
              >
                {isExtractPending ? 'Generating...' : 'Generate Extractor'}
              </Button>
            </Stack>
          </FormControl>
        )}
      </Stack>

      {editableExtractor && (
        <Box sx={{ mt: 2, gap: 2 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'custom.textBlue',
              mb: 2,
            }}
          >
            Data Extraction Preview
          </Typography>
          <JsonEditor
            data={editableExtractor}
            onUpdate={({ newData }) => handleJsonChange(newData)}
            showArrayIndices={false}
            enableClipboard={false}
            restrictTypeSelection={true}
            showCollectionCount={false}
            maxWidth="100%"
          />
          <ExtractorVariables properties={editableExtractor.properties} />
          <Button
            variant="contained"
            onClick={() => void handleSaveExtractor()}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            {isSavePending ? 'Saving...' : 'Save Extractor'}
          </Button>
        </Box>
      )}
      <MessagePopup alert={alert} setAlert={setAlert} />
      {/* Delete Modal */}
      <DeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDelete={() => void handleDeleteExtractor()}
        isPending={false}
        title="Delete Extractor"
        description="Are you sure you want to delete this file? This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default memo(GenerateExtractor);
